/* verqo · Buyer Audience Patterns Preview
   Three navigable mocks to compare how the buyer audience could be presented
   relative to the seller landing.

      A — Single landing + anchor section ("For buyers" scrolls to a section)
      B — Audience toggle in hero (one URL, swap content)
      C — Sibling sites (separate /buyers page, same shared nav/footer)
*/
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, Brain, Layers, BadgePercent, CircleDot } from "lucide-react";
import { VerqoLockup } from "../../components/VerqoLockup";

const BRAND_FG = "#0c1612";
const BRAND_ACCENT = "#dcff3a";

// ─────────────────────────── SHARED CHROME ─────────────────────────────
function Eyebrow({ children, color = "lime" }) {
  const c = color === "lime" ? "text-lime-700" : "text-slate-500";
  return (
    <div className={`inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] ${c}`}>
      <CircleDot className="w-2.5 h-2.5 fill-current" />
      {children}
    </div>
  );
}

// Reusable mini "navbar" — props let us customize per pattern
function MockNav({ links, cta = "Get paid today", showSignIn = true, persona = null, onPersonaSwitch }) {
  return (
    <header className="bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <VerqoLockup size="sm" />
          <span className="ml-1 text-[10.5px] text-slate-500 hidden md:inline">· Wholesale, instant.</span>
        </div>

        {/* Centre: nav OR audience toggle */}
        {persona ? (
          <div className="inline-flex p-1 rounded-full border border-slate-300 bg-white text-[11.5px]" data-testid="hero-persona-toggle">
            <button
              onClick={() => onPersonaSwitch("seller")}
              className={`px-3.5 py-1.5 rounded-full transition-all ${persona === "seller" ? "bg-slate-900 text-white" : "text-slate-500"}`}
              data-testid="nav-toggle-seller"
            >
              I'm a Seller
            </button>
            <button
              onClick={() => onPersonaSwitch("buyer")}
              className={`px-3.5 py-1.5 rounded-full transition-all ${persona === "buyer" ? "bg-slate-900 text-white" : "text-slate-500"}`}
              data-testid="nav-toggle-buyer"
            >
              I'm a Buyer
            </button>
          </div>
        ) : (
          <nav className="hidden md:flex items-center gap-7 text-[12px] text-slate-600">
            {links.map((l) => (
              <a key={l.label} href={l.href} className={`hover:text-slate-900 transition-colors ${l.active ? "text-slate-900 font-semibold" : ""}`}>
                {l.label}
              </a>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-3">
          {showSignIn && <a href="#sign-in" className="text-[12px] text-slate-600 hover:text-slate-900 hidden md:inline">Sign in</a>}
          <a href="#cta" className="text-[12px] px-3.5 py-2 rounded-md text-white font-medium" style={{ background: BRAND_FG }}>
            {cta}
          </a>
        </div>
      </div>
    </header>
  );
}

function MockFooter() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50/50 px-6 py-8 mt-12">
      <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <VerqoLockup size="sm" />
          <span className="text-[10.5px] text-slate-400">© 2026 · Wholesale, instant.</span>
        </div>
        <div className="flex gap-5 text-[11.5px] text-slate-500">
          <a href="#">Privacy</a><a href="#">Terms</a><a href="#">Security</a>
        </div>
      </div>
    </footer>
  );
}

// Hero blocks reused across patterns
function SellerHero({ compact = false }) {
  return (
    <section className={`px-6 ${compact ? "py-12" : "py-20 md:py-24"}`}>
      <div className="max-w-6xl mx-auto">
        <Eyebrow>For wholesale sellers</Eyebrow>
        <h1 className={`mt-4 font-semibold tracking-tight text-slate-900 ${compact ? "text-3xl md:text-4xl" : "text-4xl md:text-6xl"} leading-[0.98]`}>
          Stop waiting <span className="bg-lime-300 px-2 -mx-1 rounded">60 days</span><br />to get paid.
        </h1>
        <p className={`mt-5 max-w-xl text-slate-600 ${compact ? "text-[14px]" : "text-[16px] md:text-[17px]"} leading-relaxed`}>
          verqo turns every Net-30/60/90 invoice into same-day cash — without losing your buyer relationships.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <a href="/register?as=seller" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-white text-sm font-semibold" style={{ background: BRAND_FG }}>
            Get paid today <ArrowRight className="w-4 h-4" />
          </a>
          <a href="#how" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm">See how it works</a>
        </div>
      </div>
    </section>
  );
}

function BuyerHero({ compact = false }) {
  return (
    <section className={`px-6 ${compact ? "py-12" : "py-20 md:py-24"}`}>
      <div className="max-w-6xl mx-auto">
        <Eyebrow color="slate">For wholesale buyers</Eyebrow>
        <h1 className={`mt-4 font-semibold tracking-tight text-slate-900 ${compact ? "text-3xl md:text-4xl" : "text-4xl md:text-6xl"} leading-[0.98]`}>
          One credit line.<br /><span className="text-slate-400">Every supplier.</span>
        </h1>
        <p className={`mt-5 max-w-xl text-slate-600 ${compact ? "text-[14px]" : "text-[16px] md:text-[17px]"} leading-relaxed`}>
          Apply once. Get Net-30/60/90 credit that's honored at every supplier on verqo. Approved in seconds. No paper trade references.
        </p>
        <div className="mt-7 flex flex-wrap gap-3 items-center">
          <a href="/register?as=buyer" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-white text-sm font-semibold" style={{ background: BRAND_FG }}>
            Sign up — it's free <ArrowRight className="w-4 h-4" />
          </a>
          <a href="/login" className="text-[13px] text-slate-600 hover:text-slate-900">Already on verqo? Sign in →</a>
        </div>
      </div>
    </section>
  );
}

function BuyerBullets() {
  const items = [
    { i: Brain,        h: "Approved in seconds",    b: "AI-native underwriting. No paper trade references. No multi-week waits." },
    { i: Layers,       h: "Portable trust",         b: "Your perfect payment record unlocks terms at every supplier on verqo." },
    { i: BadgePercent, h: "Pay $0 to verqo",        b: "No subscription. No application fees. Suppliers cover the cost." },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
      {items.map(({ i: Icon, h, b }) => (
        <div key={h} className="rounded-xl border border-slate-200 bg-white p-5">
          <Icon className="w-4 h-4 text-lime-600" />
          <div className="mt-3 text-[14px] font-semibold text-slate-900">{h}</div>
          <p className="mt-1.5 text-[12.5px] text-slate-600 leading-relaxed">{b}</p>
        </div>
      ))}
    </div>
  );
}

// Filler "How it works" block reused across patterns
function HowItWorks() {
  return (
    <section id="how" className="px-6 py-20 bg-slate-50 border-y border-slate-200">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>How it works</Eyebrow>
        <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-slate-900">Three steps. One ledger.</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {["Connect your store", "Approve credit terms", "Get paid in seconds"].map((s, i) => (
            <div key={s} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Step {i + 1}</div>
              <div className="mt-2 text-[15px] font-semibold text-slate-900">{s}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────── PATTERN A ─────────────────────────────────
function PatternA() {
  const buyersRef = useRef(null);
  const onBuyersClick = (e) => {
    e.preventDefault();
    buyersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="border border-slate-300 rounded-xl overflow-hidden bg-white" data-testid="pattern-a-mock">
      <MockNav
        links={[
          { label: "The problem", href: "#" },
          { label: "The OS", href: "#" },
          { label: "Pricing", href: "#" },
          { label: "For buyers", href: "#for-buyers", onClick: onBuyersClick },
        ].map(l => l.label === "For buyers" ? { ...l, href: "#for-buyers" } : l)}
      />
      <SellerHero compact />
      <HowItWorks />

      {/* The new buyers section sitting inline */}
      <section id="for-buyers" ref={buyersRef} className="px-6 py-20 bg-gradient-to-b from-slate-50 to-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
            <div>
              <Eyebrow color="slate">For wholesale buyers</Eyebrow>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-[0.98]">
                One credit line.<br />
                <span className="text-slate-400">Every supplier.</span>
              </h2>
              <p className="mt-5 max-w-xl text-slate-600 text-[14.5px] leading-relaxed">
                Apply once. Net-30/60/90 honored at every supplier on verqo. Approved in seconds. No paper trade references.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 items-center">
                <a href="/register?as=buyer" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-white text-sm font-semibold" style={{ background: BRAND_FG }} data-testid="a-buyer-cta">
                  Sign up — it's free <ArrowRight className="w-4 h-4" />
                </a>
                <a href="/login" className="text-[13px] text-slate-600 hover:text-slate-900">Sign in →</a>
              </div>
            </div>
            <BuyerBullets />
          </div>
        </div>
      </section>

      <MockFooter />
    </div>
  );
}

// ─────────────────────────── PATTERN B ─────────────────────────────────
function PatternB() {
  const [persona, setPersona] = useState("seller");
  return (
    <div className="border border-slate-300 rounded-xl overflow-hidden bg-white" data-testid="pattern-b-mock">
      <MockNav
        links={[]}
        persona={persona}
        onPersonaSwitch={setPersona}
        cta={persona === "seller" ? "Get paid today" : "Get instant credit"}
      />
      <AnimatePresence mode="wait">
        {persona === "seller" ? (
          <motion.div key="s" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
            <SellerHero compact />
          </motion.div>
        ) : (
          <motion.div key="b" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
            <BuyerHero compact />
          </motion.div>
        )}
      </AnimatePresence>

      <section className="px-6 py-10 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto text-[13px] text-slate-600">
          <strong className="text-slate-900">{persona === "seller" ? "Sellers" : "Buyers"}</strong> · all downstream sections (Problem, The OS, Pricing) swap based on the toggle. One URL. Same nav. Same footer.
        </div>
      </section>

      <MockFooter />
    </div>
  );
}

// ─────────────────────────── PATTERN C ─────────────────────────────────
function PatternC() {
  const [activePage, setActivePage] = useState("sellers");
  return (
    <div className="border border-slate-300 rounded-xl overflow-hidden bg-white" data-testid="pattern-c-mock">
      <MockNav
        links={[
          { label: "For sellers", href: "#", active: activePage === "sellers", onClick: () => setActivePage("sellers") },
          { label: "For buyers",  href: "#", active: activePage === "buyers",  onClick: () => setActivePage("buyers")  },
          { label: "Pricing", href: "#" },
        ].map(l => l.onClick ? { ...l, href: "#", onClickHandler: l.onClick } : l)}
      />
      {/* Real navlinks need click handlers — use a thin override row */}
      <div className="px-6 py-2 bg-slate-50/70 border-b border-slate-200 text-[11px] text-slate-500 flex items-center gap-6">
        <span className="uppercase tracking-widest text-[9.5px]">Switch view:</span>
        <button onClick={() => setActivePage("sellers")} className={`underline-offset-2 ${activePage === "sellers" ? "text-slate-900 font-semibold underline" : ""}`}>
          /sellers (default)
        </button>
        <button onClick={() => setActivePage("buyers")} className={`underline-offset-2 ${activePage === "buyers" ? "text-slate-900 font-semibold underline" : ""}`}>
          /buyers
        </button>
        <span className="ml-auto text-[10px] text-slate-400">URL changes · nav stays · footer stays</span>
      </div>

      <AnimatePresence mode="wait">
        {activePage === "sellers" ? (
          <motion.div key="s" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <SellerHero compact />
            <HowItWorks />
          </motion.div>
        ) : (
          <motion.div key="b" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <BuyerHero compact />
            <section className="px-6 py-16">
              <div className="max-w-6xl mx-auto"><BuyerBullets /></div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      <MockFooter />
    </div>
  );
}

// ─────────────────────────── HUB / PAGE ────────────────────────────────
const PATTERNS = [
  {
    id: "a",
    name: "Pattern A · Single landing + section",
    sub: "Buyers live as a section on the main landing. Nav has 'For buyers' as a smooth-scroll anchor.",
    pros: ["Simplest — one source of truth", "Instant brand cohesion", "Fastest to ship + maintain", "Best for stub-stage buyer story"],
    cons: ["Both audiences share one funnel", "Less room for buyer-specific proof"],
    recommendation: "strong",
    badge: "RECOMMENDED FOR Q1",
    component: <PatternA />,
  },
  {
    id: "b",
    name: "Pattern B · Audience toggle in hero",
    sub: "One URL, but a Seller↔Buyer toggle swaps the hero + CTAs + downstream sections live.",
    pros: ["Bold first impression — addresses both audiences", "Single URL — shareable", "Modern fintech pattern (Mercury, Plaid)"],
    cons: ["Two heroes compete for the same space", "Engineering: every section needs a buyer variant", "Default persona choice is a brand decision"],
    recommendation: "good",
    badge: "BOLD CHOICE",
    component: <PatternB />,
  },
  {
    id: "c",
    name: "Pattern C · Sibling sites",
    sub: "Separate /sellers and /buyers pages, fully built, sharing the same nav + footer + brand chrome.",
    pros: ["Each audience gets a full pitch", "Stripe / Brex / Ramp pattern", "Best long-term once buyer product is real"],
    cons: ["2× content to write + maintain", "Overkill until buyer experience is built (Q2 2026)", "Easy to drift apart"],
    recommendation: "later",
    badge: "FOR Q2 2026 LAUNCH",
    component: <PatternC />,
  },
];

export default function BuyerPatternsPreview() {
  const [view, setView] = useState("hub"); // hub | a | b | c
  const [pick, setPick] = useState(null);

  useEffect(() => { document.title = "verqo · Buyer audience patterns"; }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [view]);

  if (view !== "hub") {
    const p = PATTERNS.find(x => x.id === view);
    return (
      <div className="min-h-screen bg-[#fafaf7]">
        <div className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-3 flex items-center justify-between">
          <button onClick={() => setView("hub")} className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 hover:text-slate-900" data-testid="back-to-hub">
            <ArrowLeft className="w-4 h-4" /> Back to all patterns
          </button>
          <div className="flex items-center gap-3">
            <span className="text-[10.5px] uppercase tracking-widest text-slate-400">{p.badge}</span>
            <span className="text-[12.5px] font-semibold text-slate-900">{p.name}</span>
          </div>
          <div className="flex items-center gap-2">
            {PATTERNS.map(x => (
              <button key={x.id} onClick={() => setView(x.id)} className={`px-2.5 py-1 rounded-md text-[11px] ${x.id === view ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`switch-to-${x.id}`}>
                {x.id.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <main className="px-6 py-8 max-w-7xl mx-auto">
          <div className="mb-6 text-[13px] text-slate-600 max-w-3xl">{p.sub}</div>
          {p.component}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf7]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Site architecture · decision</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mt-1">Where should buyers live?</h1>
          <p className="mt-3 text-[14px] text-slate-600 max-w-2xl leading-relaxed">
            Three patterns. Each one is fully interactive — click into a card to walk the mock,
            switch between patterns from the top bar, then pick one at the bottom.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PATTERNS.map((p) => {
            const recColor = p.recommendation === "strong" ? "bg-lime-100 text-lime-800" : p.recommendation === "good" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600";
            return (
              <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm flex flex-col" data-testid={`hub-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="text-[10.5px] uppercase tracking-[0.2em] text-slate-500">Pattern {p.id.toUpperCase()}</div>
                  <span className={`text-[9.5px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${recColor}`}>{p.badge}</span>
                </div>
                <h3 className="text-[18px] font-semibold text-slate-900 mt-2">{p.name.replace(/^Pattern . · /, "")}</h3>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{p.sub}</p>

                <div className="mt-5 grid grid-cols-2 gap-3 text-[12px] flex-1">
                  <div>
                    <div className="text-[9.5px] uppercase tracking-widest text-emerald-600 mb-1.5">Pros</div>
                    <ul className="space-y-1 text-slate-700">{p.pros.map(x => <li key={x}>· {x}</li>)}</ul>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-widest text-rose-600 mb-1.5">Cons</div>
                    <ul className="space-y-1 text-slate-700">{p.cons.map(x => <li key={x}>· {x}</li>)}</ul>
                  </div>
                </div>

                <button onClick={() => setView(p.id)} className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-white text-[13px] font-semibold hover:opacity-90 transition-opacity" style={{ background: BRAND_FG }} data-testid={`open-preview-${p.id}`}>
                  Open preview <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Picker */}
        <div className="mt-12 rounded-2xl bg-[#0c1612] text-white p-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-lime-300 mb-3">Decision</div>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">Which pattern do we ship?</h2>
          <div className="flex flex-wrap gap-3 mb-6">
            {PATTERNS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPick(p.id)}
                data-testid={`pick-${p.id}`}
                className={`px-5 py-3 rounded-full text-[13px] font-medium transition-all ${pick === p.id ? "bg-lime-300 text-[#0c1612]" : "bg-white/10 text-white hover:bg-white/20"}`}
              >
                Ship Pattern {p.id.toUpperCase()}
              </button>
            ))}
            <button
              onClick={() => setPick("keep")}
              data-testid="pick-keep"
              className={`px-5 py-3 rounded-full text-[13px] font-medium transition-all ${pick === "keep" ? "bg-lime-300 text-[#0c1612]" : "bg-white/10 text-white hover:bg-white/20"}`}
            >
              Keep current (stub)
            </button>
          </div>
          {pick && (
            <p className="text-[14px] text-white/70" data-testid="pick-confirmation">
              Selected: <span className="text-lime-300 font-medium">{pick === "keep" ? "Keep current stub" : `Pattern ${pick.toUpperCase()}`}</span> · reply "ship {pick === "keep" ? "nothing" : `pattern ${pick.toLowerCase()}`}" to deploy.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
